"use client"

import { useEffect, useMemo, useState } from "react"
import Link from "next/link"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Calendar, Clock, ArrowRight, User } from "lucide-react"
import { getApiBase } from "@/lib/api-public"

type Article = {
  id: string
  slug: string
  title: string
  excerpt: string
  date: string
  readTime: string
  category: string
  tags: string[]
  featured: boolean
}

type NotesDocMeta = {
  slug: string
  title: string
  fileName: string
  updatedAt: string
}

function articleHref(article: Article) {
  const key = article.slug?.trim() || article.id
  return `/articles/${encodeURIComponent(key)}`
}

type Props = {
  docs: NotesDocMeta[]
}

export function ArticlesFeed({ docs }: Props) {
  const [articles, setArticles] = useState<Article[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const apiBase = useMemo(() => getApiBase(), [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    fetch(`${apiBase}/api/articles`, { cache: "no-store" })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        return res.json()
      })
      .then((payload) => {
        if (!cancelled) {
          setArticles(payload.articles || [])
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(
            `Could not load articles from the API (${apiBase}). Is the backend running? Set NEXT_PUBLIC_API_BASE_URL if it uses another host.`
          )
          setArticles([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [apiBase])

  const featuredArticles = articles.filter((a) => a.featured)
  const sortedAll = [...articles].sort((a, b) => (b.date || "").localeCompare(a.date || ""))
  const categories = ["All", ...new Set(articles.map((a) => a.category))]

  return (
    <main className="container mx-auto px-6 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight mb-2">Articles & Development Log</h1>
        <p className="text-muted-foreground max-w-2xl">
          Follow the journey of building AI trading agents. Technical deep-dives, architecture decisions,
          and lessons learned from developing autonomous currency trading systems.
        </p>
      </div>

      {loading && (
        <p className="text-sm text-muted-foreground mb-6">Loading articles…</p>
      )}
      {error && (
        <Card className="mb-6 border-destructive/50">
          <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
        </Card>
      )}

      <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
        {categories.map((category) => (
          <button
            key={category}
            type="button"
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              category === "All"
                ? "bg-primary text-primary-foreground"
                : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {featuredArticles.length > 0 && (
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            Featured
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {featuredArticles.map((article) => (
              <Link
                key={article.id}
                href={articleHref(article)}
                className="block"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Card className="group hover:border-primary/50 transition-colors cursor-pointer h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="outline" className="text-xs">
                        {article.category}
                      </Badge>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {article.readTime}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{article.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {new Date(article.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </div>
                      <span className="text-sm font-medium text-primary flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read article <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
          All articles
        </h2>
        <div className="space-y-3">
          {sortedAll.map((article) => (
            <Link
              key={article.id}
              href={articleHref(article)}
              className="block"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Card className="group hover:border-primary/50 transition-colors cursor-pointer">
                <CardContent className="p-4 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge variant="outline" className="text-xs shrink-0">
                        {article.category}
                      </Badge>
                      {article.featured && (
                        <Badge variant="default" className="text-xs shrink-0">
                          Featured
                        </Badge>
                      )}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(article.date).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {article.readTime}
                        </span>
                      </div>
                    </div>
                    <h3 className="font-semibold group-hover:text-primary transition-colors truncate">
                      {article.title}
                    </h3>
                    <p className="text-sm text-muted-foreground truncate mt-0.5">{article.excerpt}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                </CardContent>
              </Card>
            </Link>
          ))}
          {!loading && sortedAll.length === 0 && !error && (
            <Card>
              <CardContent className="p-6 text-sm text-muted-foreground">
                No published articles yet. Use the hidden admin page to publish.
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          Documentation
        </h2>
        <div className="space-y-3">
          {docs.map((doc) => (
            <Link
              key={doc.slug}
              href={`/articles/docs/${encodeURIComponent(doc.slug)}`}
              className="block"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Card className="group hover:border-primary/50 transition-colors cursor-pointer">
                <CardContent className="p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="font-semibold group-hover:text-primary transition-colors truncate">
                      {doc.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Updated{" "}
                      {new Date(doc.updatedAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                </CardContent>
              </Card>
            </Link>
          ))}
          {docs.length === 0 && (
            <Card>
              <CardContent className="p-6 text-sm text-muted-foreground">
                No documentation markdown files found in <code>backend/agents/notes</code>.
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      <section className="mt-12 p-6 rounded-lg bg-secondary/30 border border-border">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
            <User className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold mb-1">About This Project</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              I&apos;m Tony Mudau, and this is my journey building autonomous AI trading agents for currency
              markets. These articles document the technical challenges, architectural decisions, and lessons
              learned while developing a multi-agent system that combines technical analysis, fundamental
              analysis, sentiment analysis, and risk management.
            </p>
          </div>
        </div>
      </section>
    </main>
  )
}
